import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import {
  Box,
  Button,
  Container,
  Typography,
  Stack
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import AddIcon from '@mui/icons-material/AddOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';

import FMSToolbar from 'src/common/FMSToolbar';
import FilterSelectInput from 'src/common/FilterSelectInput';
import TransactionListTable from 'src/components/transactions/TransactionListTable';
import { setTransactionType } from 'src/redux/transactionSlice';
import { getAllTanks } from 'src/redux/tankSlice';
import { getAllDispensers } from 'src/redux/dispenserSlice';
import { getAllSites } from 'src/redux/siteSlice';
import NewTransaction from 'src/components/transactions/NewTransaction';
import ExternalTransactionBulkUpload from 'src/components/transactions/externalTransactionBulkUpload';
import ReportFilterModal from 'src/components/reports/ReportFilterModal';
import DateFilterField from 'src/common/formFields/DateFilterField';
import TransactionFilters from 'src/components/transactions/TransactionFilters';
import getActiveTransactionFilters, {
  getDraftTransactionDateRange
} from 'src/components/transactions/transactionFilterUtils';

import {
  DATE_FORMAT,
  REPORT_FILTER_OPTIONS,
  TRANSACTION_TYPES,
  USER_ROLES
} from 'src/utils/constants';

import {
  reportFormModel,
  reportFormInitialValues,
  reportValidationSchema
} from 'src/components/reports/reportFormModel';

import {
  inwardReportFormModel,
  inwardReportFormInitialValues,
  inwardReportValidationSchema
} from 'src/components/reports/inwardReportFormModel';
import {
  externalReportFormModel,
  externalReportFormInitialValues,
  externalReportValidationSchema
} from 'src/components/reports/externalReportFormModel';

import {
  getFilterOptions,
  getReportDownloadURL,
  getReportFormTitle,
  getSearchPlaceholder
} from 'src/components/transactions/transactionService';
import useAuth from 'src/context/AuthContext';
import { setSuccessInfo } from 'src/redux/modalSlice';
import { hasAdminLikeAccess } from 'src/utils/getInitials';
import useAdminSiteFilter from 'src/hooks/useAdminSiteFilter';
import { FLEET_ONLY_TRANSACTION_TYPE, isFleetOnlyModeEnabled } from 'src/utils/appMode';
import {
  getStoredUserTimezone,
  nowInTimezone,
  parseInStoredTimezone
} from 'src/utils/timezoneUtils';
import {
  DEFAULT_TRANSACTION_TYPE,
  parseTransactionQuery,
  serializeTransactionQuery
} from './transactionQuery';

const EMPTY_DATE_RANGE = { start: null, end: null };

const Transactions = ({ location, history }) => {
  const initialQuery = parseTransactionQuery(location.search);
  const dispatch = useDispatch();
  const { getUser } = useAuth();
  const {
    isAdmin,
    siteFilter,
    setSiteFilter,
    siteOptions
  } = useAdminSiteFilter(initialQuery.site || 'all');
  const user = getUser();
  const hasAdminAccess = hasAdminLikeAccess(getUser);
  const isApprover = user?.role === USER_ROLES.approver;
  const { transactionType, transactionList } = useSelector((state) => state.transactions);
  const { allTanks = [] } = useSelector((state) => state.tanks);
  const { allDispensers = [] } = useSelector((state) => state.dispensers);
  const { allSites = [] } = useSelector((state) => state.sites);
  const { appSettings } = useSelector((state) => state.settings);
  const isFleetOnlyMode = isFleetOnlyModeEnabled(appSettings);

  const [openModal, setOpenModal] = useState(false);
  const [openBulkUploadModal, setOpenBulkUploadModal] = useState(false);
  const [transactionRefreshKey, setTransactionRefreshKey] = useState(0);
  const [openReportModal, setOpenReportModal] = useState(false);
  const [searchText, setSearchText] = useState(initialQuery.search);
  const [filterDateRange, setFilterDateRange] = useState({
    start: initialQuery.start || null,
    end: initialQuery.end || null
  });
  const [tankFilter, setTankFilter] = useState(initialQuery.tank || 'all');
  const [externalAppliedVehicle, setExternalAppliedVehicle] = useState(initialQuery.vehicle);
  const [externalAppliedDateRange, setExternalAppliedDateRange] = useState({
    start: initialQuery.start || null,
    end: initialQuery.end || null
  });
  const [externalAppliedSite, setExternalAppliedSite] = useState(initialQuery.site || 'all');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [draftVehicle, setDraftVehicle] = useState('');
  const [draftStartDateTime, setDraftStartDateTime] = useState(null);
  const [draftEndDateTime, setDraftEndDateTime] = useState(null);
  const [draftSite, setDraftSite] = useState('all');
  const [draftTank, setDraftTank] = useState('all');
  const [filterError, setFilterError] = useState('');
  const [pageTransactionType, setPageTransactionType] = useState(
    initialQuery.type || DEFAULT_TRANSACTION_TYPE
  );
  const effectiveTransactionType = isFleetOnlyMode
    ? FLEET_ONLY_TRANSACTION_TYPE
    : pageTransactionType;
  const isSales = effectiveTransactionType !== TRANSACTION_TYPES[1].id;
  const isIssued = effectiveTransactionType === TRANSACTION_TYPES[0].id;
  const isInward = effectiveTransactionType === TRANSACTION_TYPES[1].id;
  const isExternal = effectiveTransactionType === TRANSACTION_TYPES[3].id;
  const canAddTransaction = isInward ? hasAdminAccess || isApprover : hasAdminAccess;
  const canUploadExternalTransactions = isExternal
    && [
      USER_ROLES.admin,
      USER_ROLES.siteManager,
      USER_ROLES.requestor,
      USER_ROLES.approver
    ].includes(user?.role)
    && Boolean(appSettings?.external_transaction_upload_enabled);
  const isDropdownFilterView = isExternal || isInward || (isIssued && isAdmin);
  let transactionDescription = null;
  if (isFleetOnlyMode) {
    transactionDescription = 'Manage fleet inward transactions only.';
  } else if (isExternal) {
    transactionDescription = 'Manage fuel inwards, issued, and transfer records.';
  }
  const appliedSearchText = isExternal ? externalAppliedVehicle : searchText;
  const appliedDateFilter = isExternal ? externalAppliedDateRange : filterDateRange;
  const appliedSiteFilter = isExternal ? externalAppliedSite : siteFilter;
  const reportDownloadUrl = getReportDownloadURL(effectiveTransactionType);
  const filterOptions = getFilterOptions(effectiveTransactionType);
  const externalReportFilterOptions = {
    ...REPORT_FILTER_OPTIONS,
    dispenser: false,
    vehicle: false,
    vehicleGroup: false,
    assetType: false
  };
  let activeReportFormModel = inwardReportFormModel;
  let activeReportValidation = inwardReportValidationSchema;
  let activeReportInitialValues = inwardReportFormInitialValues;
  if (isExternal) {
    activeReportFormModel = externalReportFormModel;
    activeReportValidation = externalReportValidationSchema;
    activeReportInitialValues = externalReportFormInitialValues;
  } else if (isSales) {
    activeReportFormModel = reportFormModel;
    activeReportValidation = reportValidationSchema;
    activeReportInitialValues = reportFormInitialValues;
  }
  const searchPlaceholder = getSearchPlaceholder(effectiveTransactionType);

  const replaceTransactionQuery = (query) => {
    const queryString = serializeTransactionQuery(query);
    history.replace({
      pathname: location.pathname,
      search: queryString ? `?${queryString}` : ''
    });
  };

  useEffect(() => {
    const query = parseTransactionQuery(location.search);
    const restoredType = isFleetOnlyMode ? FLEET_ONLY_TRANSACTION_TYPE : query.type;
    if (pageTransactionType !== restoredType) setPageTransactionType(restoredType);
    if (transactionType !== restoredType) dispatch(setTransactionType(restoredType));

    setSearchText(query.search);
    setFilterDateRange({ start: query.start || null, end: query.end || null });
    setTankFilter(query.tank || 'all');
    setExternalAppliedVehicle(query.vehicle);
    setExternalAppliedDateRange({ start: query.start || null, end: query.end || null });
    setExternalAppliedSite(query.site || 'all');
    setSiteFilter(query.site || 'all');
  }, [dispatch, isFleetOnlyMode, location.search, pageTransactionType, setSiteFilter, transactionType]);

  const tankOptions = useMemo(() => {
    const filteredTanks = siteFilter === 'all'
      ? allTanks
      : allTanks.filter((tank) => String(tank.site) === String(siteFilter));
    return [
      { id: 'all', name: 'All Tanks' },
      ...filteredTanks.map((tank) => ({
        ...tank,
        name: tank.name || tank.tank_name || `Tank ${tank.id}`
      }))
    ];
  }, [allTanks, siteFilter]);

  const draftTankOptions = useMemo(() => {
    const filteredTanks = draftSite === 'all'
      ? allTanks
      : allTanks.filter((tank) => String(tank.site) === String(draftSite));
    return [
      { id: 'all', name: 'All Tanks' },
      ...filteredTanks.map((tank) => ({
        ...tank,
        name: tank.name || tank.tank_name || `Tank ${tank.id}`
      }))
    ];
  }, [allTanks, draftSite]);

  const activeFilters = getActiveTransactionFilters({
    isExternal,
    isIssued,
    site: appliedSiteFilter,
    tank: tankFilter,
    start: appliedDateFilter.start,
    end: appliedDateFilter.end,
    vehicle: externalAppliedVehicle
  });

  const handleSearch = (value) => {
    const nextSearch = value.trim();
    setSearchText(nextSearch);
    replaceTransactionQuery({
      type: effectiveTransactionType,
      search: nextSearch,
      site: siteFilter,
      tank: tankFilter,
      start: filterDateRange.start,
      end: filterDateRange.end
    });
  };

  const handleSiteChange = (value) => {
    setSiteFilter(value);
    replaceTransactionQuery({
      type: effectiveTransactionType,
      search: searchText,
      site: value,
      tank: tankFilter,
      start: filterDateRange.start,
      end: filterDateRange.end
    });
  };

  const handleGroupChange = (value) => {
    setFilterDateRange(EMPTY_DATE_RANGE);
    setSearchText('');
    setTankFilter('all');
    setExternalAppliedVehicle('');
    setExternalAppliedDateRange(EMPTY_DATE_RANGE);
    setExternalAppliedSite('all');
    setSiteFilter('all');
    setPageTransactionType(value);
    dispatch(setTransactionType(value));
    replaceTransactionQuery({ type: value });
  };

  useEffect(() => {
    if (isFleetOnlyMode && transactionType !== FLEET_ONLY_TRANSACTION_TYPE) {
      dispatch(setTransactionType(FLEET_ONLY_TRANSACTION_TYPE));
    }
  }, [dispatch, isFleetOnlyMode, transactionType]);

  useEffect(() => {
    if (isAdmin && isIssued && allTanks.length === 0) {
      dispatch(getAllTanks());
    }
  }, [allTanks.length, dispatch, isAdmin, isIssued]);

  useEffect(() => {
    if (openBulkUploadModal && allDispensers.length === 0) {
      dispatch(getAllDispensers());
    }
    if (openBulkUploadModal && allSites.length === 0) {
      dispatch(getAllSites());
    }
  }, [allDispensers.length, allSites.length, dispatch, openBulkUploadModal]);

  const handleExternalUploadComplete = (result) => {
    setTransactionRefreshKey((current) => current + 1);
    dispatch(setSuccessInfo({
      successMsg: `${result?.created_count || 0} external transactions created successfully.`
    }));
  };

  useEffect(() => {
    if (!isIssued) {
      setTankFilter('all');
      return;
    }
    if (allTanks.length === 0) return;

    const hasSelectedTank = tankOptions.some(
      (tank) => String(tank.id) === String(tankFilter)
    );
    if (!hasSelectedTank) setTankFilter('all');
  }, [tankFilter, tankOptions, isIssued]);

  const getDateTimeFilterString = (newValue) => (
    newValue && newValue.isValid()
      ? moment.tz(
        newValue.format(DATE_FORMAT.unixDateTime),
        DATE_FORMAT.unixDateTime,
        true,
        getStoredUserTimezone()
      ).format(DATE_FORMAT.unixDateTime)
      : null
  );

  const setDateRangeField = (fieldName, newValue) => {
    const nextDateRange = {
      ...filterDateRange,
      [fieldName]: getDateTimeFilterString(newValue)
    };
    setFilterDateRange(() => nextDateRange);
    replaceTransactionQuery({
      type: effectiveTransactionType,
      search: searchText,
      site: siteFilter,
      tank: tankFilter,
      start: nextDateRange.start,
      end: nextDateRange.end
    });
  };

  const clearDateRangeFilter = () => {
    setFilterDateRange(EMPTY_DATE_RANGE);
    replaceTransactionQuery({
      type: effectiveTransactionType,
      search: searchText,
      site: siteFilter,
      tank: tankFilter
    });
  };
  const externalVehicleOptions = useMemo(() => {
    const uniqueVehicles = Array.from(
      new Set(
        (transactionList || [])
          .map((item) => item?.vehicle_number)
          .filter(Boolean)
      )
    );
    return uniqueVehicles;
  }, [transactionList]);

  const openFilterPopover = (e) => {
    setDraftVehicle(isExternal ? externalAppliedVehicle || '' : '');
    const appliedDateRange = isExternal ? externalAppliedDateRange : filterDateRange;
    const now = nowInTimezone(getStoredUserTimezone()).seconds(0).milliseconds(0);
    const draftDateRange = getDraftTransactionDateRange({
      isExternal,
      appliedDateRange,
      now
    });
    setDraftStartDateTime(draftDateRange.start);
    setDraftEndDateTime(draftDateRange.end);
    setDraftSite(isExternal ? externalAppliedSite || 'all' : siteFilter || 'all');
    setDraftTank(isExternal ? 'all' : tankFilter || 'all');
    setFilterError('');
    setFilterAnchorEl(e.currentTarget);
  };

  const closeFilterPopover = () => {
    setFilterAnchorEl(null);
  };

  const handleApplyFilters = () => {
    const hasPartialDateRange = Boolean(draftStartDateTime) !== Boolean(draftEndDateTime);
    if ((!isExternal && (!draftStartDateTime || !draftEndDateTime)) || hasPartialDateRange) {
      setFilterError('Start date time and end date time are required.');
      return;
    }
    if (draftStartDateTime && draftEndDateTime && draftEndDateTime.isBefore(draftStartDateTime)) {
      setFilterError('End date time must be after start date time.');
      return;
    }
    const appliedDateRange = {
      start: draftStartDateTime?.format(DATE_FORMAT.unixDateTime) || null,
      end: draftEndDateTime?.format(DATE_FORMAT.unixDateTime) || null
    };
    if (isExternal) {
      setExternalAppliedVehicle(draftVehicle || '');
      setExternalAppliedDateRange(appliedDateRange);
      setExternalAppliedSite(draftSite || 'all');
      replaceTransactionQuery({
        type: effectiveTransactionType,
        vehicle: draftVehicle || '',
        site: draftSite || 'all',
        start: appliedDateRange.start,
        end: appliedDateRange.end
      });
    } else {
      setFilterDateRange(appliedDateRange);
      setSiteFilter(draftSite || 'all');
      setTankFilter(draftTank || 'all');
      replaceTransactionQuery({
        type: effectiveTransactionType,
        search: searchText,
        site: draftSite || 'all',
        tank: draftTank || 'all',
        start: appliedDateRange.start,
        end: appliedDateRange.end
      });
    }
    closeFilterPopover();
  };

  const handleClearDraftFilters = () => {
    setDraftVehicle('');
    setDraftStartDateTime(null);
    setDraftEndDateTime(null);
    setDraftSite('all');
    setDraftTank('all');
    setFilterError('');

    if (isExternal) {
      setExternalAppliedVehicle('');
      setExternalAppliedDateRange(EMPTY_DATE_RANGE);
      setExternalAppliedSite('all');
      replaceTransactionQuery({ type: effectiveTransactionType });
    } else {
      setFilterDateRange(EMPTY_DATE_RANGE);
      setSiteFilter('all');
      setTankFilter('all');
      replaceTransactionQuery({ type: effectiveTransactionType });
    }

    closeFilterPopover();
  };

  return (
    <>
      <Helmet>
        <title>Transactions | Retrack FMS</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: isFleetOnlyMode ? '#F3F8FC' : '#FFFFFF',
          minHeight: 'calc(100vh - 64px)',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          overflow: { xs: 'auto', md: 'hidden' },
          py: 2
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >

          {/* HEADER */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: isExternal ? 'flex-start' : 'center' }}
            spacing={2}
            sx={{ py: 2, mb: 2, flexShrink: 0 }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 800, mb: 0.5 }}
              >
                TRANSACTIONS
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={isExternal ? { whiteSpace: 'nowrap' } : undefined}
              >
                {transactionDescription || (
                  <>
                    Manage fuel inwards, issued,
                    <br />
                    and transfer records.
                  </>
                )}
              </Typography>
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{
                width: { xs: '100%', md: isExternal ? '100%' : 'auto' }
              }}
            >
              <FMSToolbar
                showSearch={!isExternal}
                searchValue={searchText}
                showGroup={!isExternal && !isFleetOnlyMode}
                placeholder={searchPlaceholder}
                actionName="Export"
                actionIcon={<DownloadIcon />}
                groupData={isFleetOnlyMode
                  ? TRANSACTION_TYPES.filter((type) => type.id === FLEET_ONLY_TRANSACTION_TYPE)
                  : TRANSACTION_TYPES}
                groupType={effectiveTransactionType}
                groupLabel="Category"
                onGroupChange={handleGroupChange}
                onSearch={handleSearch}
                onAction={() => {
                  if (isExternal) {
                    setOpenReportModal(true);
                  } else {
                    setOpenReportModal(true);
                  }
                }}
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  '& .MuiCardContent-root': {
                    p: 1.5,
                    ...(isExternal && {
                      pl: 1.5,
                      pr: 1.5
                    })
                  },
                  '& .MuiCardContent-root > .MuiStack-root': {
                    width: '100%',
                    alignItems: { xs: 'stretch', md: isExternal ? 'stretch' : 'flex-start' },
                    ...(isExternal && {
                      justifyContent: 'space-between',
                      columnGap: 2
                    }),
                    flexWrap: { xs: 'nowrap', md: 'wrap', lg: 'nowrap' },
                    rowGap: 1.25,
                    columnGap: 1.25
                  },
                  '& .MuiCardContent-root > .MuiStack-root > .MuiStack-root:first-of-type': {
                    minWidth: 0,
                    flex: '1 1 auto',
                    width: '100%',
                    ...(isExternal && {
                      justifyContent: 'space-between'
                    }),
                    flexWrap: { xs: 'nowrap', md: 'wrap', lg: 'nowrap' },
                    rowGap: 1.25,
                    columnGap: isExternal ? 2 : 1.25
                  },
                  '& .MuiCardContent-root > .MuiStack-root > .MuiStack-root:last-of-type': {
                    flexShrink: 0,
                    width: {
                      xs: '100%',
                      md: isExternal ? 190 : 'auto'
                    },
                  },
                  '& .MuiTextField-root': {
                    width: {
                      xs: '100%',
                      sm: '100%',
                      md: isIssued ? 320 : 240,
                      lg: isIssued ? 360 : 280
                    },
                    minWidth: {
                      md: isIssued ? 300 : 220
                    },
                    flex: {
                      md: isIssued ? '1 1 320px' : '1 1 240px'
                    }
                  },
                  '& .MuiFormControl-root': {
                    width: {
                      xs: '100%',
                      sm: '100%',
                      md: 150,
                      lg: 170
                    },
                    minWidth: {
                      md: 140
                    }
                  },
                  '& .MuiInputBase-root': {
                    height: 38
                  },
                  '& .MuiFormControl-root .MuiInputLabel-root': {
                    top: 0
                  },
                  '& .MuiFormControl-root .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: '38px',
                    boxSizing: 'border-box'
                  },
                  '& .MuiButton-root': {
                    minHeight: 38
                  },
                  '& .MuiCardContent-root > .MuiStack-root > .MuiStack-root:last-of-type .MuiButton-root': {
                    height: 38,
                    px: isInward ? 1.0 : 2.5,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    ...(isInward && {
                      minWidth: 50
                    })
                  },
                  '& .MuiButton-contained': {
                    whiteSpace: 'nowrap'
                  },
                  '& .MuiDivider-vertical': {
                    display: {
                      md: 'none',
                      lg: 'block'
                    }
                  }
                }}
              >
                {isDropdownFilterView ? (
                  <>
                    {isExternal && (
                      <FilterSelectInput
                        label="Category"
                        name="category"
                        value={effectiveTransactionType}
                        data={TRANSACTION_TYPES}
                        onInputChange={handleGroupChange}
                        sx={{
                          width: {
                            xs: '100%',
                            sm: '100%',
                            md: 150,
                            lg: 170
                          },
                          minWidth: {
                            md: 140
                          },
                          mr: 0
                        }}
                      />
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FilterAltOutlinedIcon />}
                      onClick={openFilterPopover}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        minHeight: 38,
                        borderRadius: 2,
                        width: {
                          xs: '100%',
                          sm: '100%',
                          md: 140,
                          lg: 180
                        },
                        minWidth: {
                          md: 130
                        },
                        whiteSpace: 'nowrap',
                        mr: 0
                      }}
                    >
                      Filters
                      {activeFilters.length ? ` ${activeFilters.length}` : ''}
                    </Button>
                    {canAddTransaction && !isIssued && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenModal(true)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          minHeight: 38,
                          px: 2,
                          borderRadius: 2,
                          width: { xs: '100%', sm: '100%', md: 'auto' },
                          flexShrink: 0
                        }}
                      >
                        Add Transaction
                      </Button>
                    )}
                    {canUploadExternalTransactions && (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<UploadFileIcon />}
                        onClick={() => setOpenBulkUploadModal(true)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          minHeight: 38,
                          px: 2,
                          borderRadius: 2,
                          width: { xs: '100%', sm: '100%', md: 'auto' },
                          flexShrink: 0
                        }}
                      >
                        Upload Excel
                      </Button>
                    )}
                    <TransactionFilters
                      anchorEl={filterAnchorEl}
                      onClose={closeFilterPopover}
                      onApply={handleApplyFilters}
                      onClear={handleClearDraftFilters}
                      isExternal={isExternal}
                      isIssued={isIssued}
                      siteOptions={siteOptions}
                      tankOptions={draftTankOptions}
                      externalVehicleOptions={externalVehicleOptions}
                      draftVehicle={draftVehicle}
                      setDraftVehicle={setDraftVehicle}
                      draftStartDateTime={draftStartDateTime}
                      setDraftStartDateTime={(value) => {
                        setDraftStartDateTime(value);
                        setFilterError('');
                      }}
                      draftEndDateTime={draftEndDateTime}
                      setDraftEndDateTime={(value) => {
                        setDraftEndDateTime(value);
                        setFilterError('');
                      }}
                      draftSite={draftSite}
                      setDraftSite={setDraftSite}
                      draftTank={draftTank}
                      setDraftTank={setDraftTank}
                      filterError={filterError}
                    />
                  </>
                ) : (
                  <>
                    <DateFilterField
                      label="Start Date Time *"
                      value={filterDateRange.start
                        ? parseInStoredTimezone(filterDateRange.start, DATE_FORMAT.unixDateTime)
                        : null}
                      onlyDate={false}
                      onDateChange={(newValue) => setDateRangeField('start', newValue)}
                      onClearFilter={clearDateRangeFilter}
                      pickerProps={{
                        maxDateTime: filterDateRange.end
                          ? parseInStoredTimezone(filterDateRange.end, DATE_FORMAT.unixDateTime)
                          : undefined
                      }}
                      sx={{
                        width: {
                          xs: '100%',
                          sm: '100%',
                          md: 190,
                          lg: 220
                        },
                        minWidth: {
                          md: 180
                        }
                      }}
                    />
                    <DateFilterField
                      label="End Date Time *"
                      value={filterDateRange.end
                        ? parseInStoredTimezone(filterDateRange.end, DATE_FORMAT.unixDateTime)
                        : null}
                      onlyDate={false}
                      onDateChange={(newValue) => setDateRangeField('end', newValue)}
                      onClearFilter={clearDateRangeFilter}
                      pickerProps={{
                        minDateTime: filterDateRange.start
                          ? parseInStoredTimezone(filterDateRange.start, DATE_FORMAT.unixDateTime)
                          : undefined
                      }}
                      sx={{
                        width: {
                          xs: '100%',
                          sm: '100%',
                          md: 190,
                          lg: 220
                        },
                        minWidth: {
                          md: 180
                        }
                      }}
                    />
                    {isAdmin && (
                    <FilterSelectInput
                      label="Site"
                      name="site"
                      value={siteFilter}
                      data={siteOptions}
                      onInputChange={handleSiteChange}
                      sx={{
                        width: {
                          xs: '100%',
                          sm: '100%',
                          md: 160,
                          lg: 180
                        },
                        minWidth: {
                          md: 150
                        },
                        mr: 0
                      }}
                    />
                    )}
                    {isInward && canAddTransaction && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenModal(true)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        minHeight: 38,
                        px: 2,
                        borderRadius: 2,
                        width: { xs: '100%', sm: '100%', md: 'auto' },
                        flexShrink: 0
                      }}
                    >
                      Add Transaction
                    </Button>
                    )}
                  </>
                )}
              </FMSToolbar>
            </Stack>
          </Stack>

          {/* TOOLBAR (Everything inside same box) */}

          {/* TABLE */}
          <Box
            sx={{
              pt: 3,
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <TransactionListTable
              refreshData={transactionRefreshKey || location.state?.fromDashboard}
              searchText={appliedSearchText}
              dateFilter={appliedDateFilter}
              siteFilter={appliedSiteFilter}
              tankFilter={isIssued && isAdmin ? tankFilter : 'all'}
              isSales={isSales}
              transactionTypeOverride={effectiveTransactionType}
            />
          </Box>

          {/* MODALS */}
          <NewTransaction
            open={openModal}
            handleClose={() => setOpenModal(false)}
          />

          <ExternalTransactionBulkUpload
            open={openBulkUploadModal}
            onClose={() => setOpenBulkUploadModal(false)}
            dispensers={allDispensers}
            sites={allSites}
            onComplete={handleExternalUploadComplete}
          />

          <ReportFilterModal
            open={openReportModal}
            title={getReportFormTitle(effectiveTransactionType)}
            formModel={activeReportFormModel}
            formValidation={activeReportValidation}
            formInitValues={activeReportInitialValues}
            filterConfig={isExternal ? externalReportFilterOptions : filterOptions}
            downloadUrl={reportDownloadUrl}
            handleClose={() => setOpenReportModal(false)}
          />

        </Container>
      </Box>
    </>
  );
};

Transactions.propTypes = {
  location: PropTypes.object,
  history: PropTypes.object
};

export default Transactions;
